import { useGetCountries } from "@/actions/hooks/useGetCountries.tsx";
import { PhoneInputProps } from "@/components/Inputs/PhoneInput/PhoneInputProps";
import { PrefixOptionProps } from "@/components/Inputs/PhoneInput/PrefixOptionProps.ts";
import { useClickOutsideListener } from "@/components/Inputs/PhoneInput/useClickOutsideListener";
import { usePhonePrefix } from "@/components/Inputs/PhoneInput/usePhonePrefix";
import { usePhoneRehydrate } from "@/components/Inputs/PhoneInput/usePhoneRehydrate";
import { useSyncPhoneToForm } from "@/components/Inputs/PhoneInput/useSyncPhoneToForm";
import { requiredRule } from "@/helpers/form/FormRules";
import { mergeRefs } from "@/helpers/mergeRefs.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext";
import { useRegisterFieldRef } from "@/stores/FieldRefsStore/useRegisterFieldRef.ts";
import { Search } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const PrefixOption = ({ option, isSelected, onSelect }: PrefixOptionProps) => (
  <div
    role="option"
    aria-selected={isSelected}
    className={["c-phone-input__prefix-option", isSelected ? "c-phone-input__prefix-option--selected" : ""]
      .filter(Boolean)
      .join(" ")}
    onClick={() => onSelect(option.iso3)}
  >
    <span className="c-phone-input__prefix-option-flag">{option.flag}</span>
    <span className="c-phone-input__prefix-option-name">{option.name}</span>
    <span className="c-phone-input__prefix-option-code">{option.dialCode}</span>
  </div>
);

export const PhoneInput = ({
  name,
  watchedCountryIso3,
  error,
  register,
  setValue,
  trigger,
  watch,
}: PhoneInputProps) => {
  const { t } = useTranslation();
  const { shadowRoot } = useShadowRoot();
  const { countries } = useGetCountries();

  const [rawNumber, setRawNumber] = useState("");
  const [isTouched, setIsTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isInitialized = useRef(false);

  const {
    selectedIso3,
    selectedEntry,
    isOpen,
    search,
    priorityOptions,
    filteredOptions,
    dropdownRef,
    searchRef,
    setSearch,
    handleToggle,
    handleSelect: handlePrefixSelect,
    handleClickOutside,
  } = usePhonePrefix({ watchedCountryIso3, isPhoneEmpty: !rawNumber.trim(), countries });

  usePhoneRehydrate({
    name,
    watch,
    onPrefixSelect: handlePrefixSelect,
    onRawNumberChange: setRawNumber,
    isInitialized,
  });

  useSyncPhoneToForm({
    name,
    rawNumber,
    selectedEntry,
    isTouched,
    isInitialized,
    setValue,
    trigger,
  });

  useClickOutsideListener(shadowRoot, handleClickOutside);

  const registerFieldRef = useRegisterFieldRef(name);
  const rootRef = useMemo(() => mergeRefs(dropdownRef, registerFieldRef), [dropdownRef, registerFieldRef]);

  // Hidden input registration — validation runs against the combined value.
  const registration = register(name, {
    ...requiredRule,
    pattern: {
      value: /^\+\d{7,15}$/,
      message: t("invalidPhone"),
    },
  });

  const currentValue = watch(name);

  const rootClasses = [
    "c-floating-select",
    "c-phone-input",
    "c-floating-select--floating",
    isFocused && "c-floating-select--focused",
    error && "c-floating-select--error",
    error && "c-phone-input--error",
    isOpen && "c-phone-input--prefix-open",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClasses} ref={rootRef}>
      {/* Hidden input — holds the combined value for RHF */}
      <input type="hidden" {...registration} value={currentValue || ""} />

      <div className="c-phone-input__field">
        <button
          type="button"
          className={["c-phone-input__prefix-trigger", isOpen ? "c-phone-input__prefix-trigger--open" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={handleToggle}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="c-phone-input__prefix-code">{selectedEntry.dialCode}</span>
          <span className="c-phone-input__prefix-chevron" aria-hidden="true">
            ▾
          </span>
        </button>

        <div className="c-phone-input__separator" aria-hidden="true" />

        <input
          type="tel"
          name="tel"
          id={`${name}-number`}
          className="c-phone-input__number"
          value={rawNumber}
          onChange={(e) => {
            setRawNumber(e.target.value);
            if (!isTouched) setIsTouched(true);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            if (!isTouched) setIsTouched(true);
          }}
          onClick={(e) => e.stopPropagation()}
          inputMode="tel"
          autoComplete="tel-national"
        />
      </div>

      <label className="c-floating-select__label" htmlFor={`${name}-number`}>
        {t(name)}
      </label>

      {isOpen && (
        <div className="c-phone-input__prefix-dropdown" role="listbox">
          <div className="c-phone-input__search-wrap">
            <Search size={14} className="c-phone-input__search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              className="c-phone-input__search"
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="c-phone-input__prefix-options">
            {!search.trim() &&
              priorityOptions.map((option) => (
                <PrefixOption
                  key={`priority-${option.iso3}`}
                  option={option}
                  isSelected={option.iso3 === selectedIso3}
                  onSelect={handlePrefixSelect}
                />
              ))}

            {!search.trim() && priorityOptions.length > 0 && (
              <div className="c-phone-input__options-divider" aria-hidden="true" />
            )}

            {filteredOptions.map((option) => (
              <PrefixOption
                key={option.iso3}
                option={option}
                isSelected={option.iso3 === selectedIso3}
                onSelect={handlePrefixSelect}
              />
            ))}

            {filteredOptions.length === 0 && (
              <div className="c-phone-input__no-results">{t("noResults")}</div>
            )}
          </div>
        </div>
      )}

      {error && error.type !== "required" && (
        <span className="c-floating-select__error">{error.message ? String(error.message) : null}</span>
      )}
    </div>
  );
};
